import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, ShieldCheck,
  ExternalLink, Play,
  StopCircle, RefreshCw, GitCommit, HatGlasses
} from 'lucide-react';
import { authenticatedFetch } from '../utils/api';
import { useNotification } from '../contexts/NotificationContext';
import StatusBadge from './StatusBadge';
import Loading, { LoadingSpinner } from './Loading';
import Modal from './Modal';
import DeepScanForm from './DeepScanForm';

/**
 * Deep Scan Modal
 * Allows users to initiate and view deep scans on a repository
 */
const DeepScanModal = ({
  isOpen,
  onClose,
  repositoryUrl,
  resultId = null,
  runnerId = null
}) => {
  const { addSuccessNotification, addErrorNotification } = useNotification();

  // State
  const [loading, setLoading] = useState(true);
  const [startingScan, setStartingScan] = useState(false);
  const [currentScan, setCurrentScan] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  
  // Scan configuration using the new reusable form
  const [scanConfig, setScanConfig] = useState({
    verifiedOnly: true,
    maxDepth: '',
    branch: '',
    deepRecovery: false
  });

  // Polling interval for running scans
  const POLL_INTERVAL = 3000; // 3 seconds

  /**
   * Fetch current scan status and history
   */
  const fetchScanData = useCallback(async () => {
    try {
      // Get repository status (latest scan)
      const statusResponse = await authenticatedFetch(
        `/deepscan/repository-status?repository_url=${encodeURIComponent(repositoryUrl)}`
      );

      if (statusResponse?.has_scan) {
        setCurrentScan(statusResponse.latest_scan);
      } else {
        setCurrentScan(null);
      }

      // Get scan history
      const historyResponse = await authenticatedFetch(
        `/deepscan/scans?repository_url=${encodeURIComponent(repositoryUrl)}&limit=10`
      );

      if (historyResponse?.scans) {
        setScanHistory(historyResponse.scans);
      }

    } catch (error) {
      console.error('Error fetching deep scan data:', error);
    } finally {
      setLoading(false);
    }
  }, [repositoryUrl]);

  // Initial load
  useEffect(() => {
    if (isOpen) {
      fetchScanData();
    }
  }, [isOpen, fetchScanData]);

  // Poll for updates when scan is running
  useEffect(() => {
    if (!isOpen || !currentScan) return;

    if (currentScan.status === 'pending' || currentScan.status === 'running') {
      const interval = setInterval(fetchScanData, POLL_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [isOpen, currentScan, fetchScanData]);

  /**
   * Start a new deep scan
   */
  const startScan = async () => {
    try {
      setStartingScan(true);
      
      const { verifiedOnly, maxDepth, branch, deepRecovery } = scanConfig;

      // Build request body with optional parameters
      const requestBody = {
        repository_url: repositoryUrl,
        result_id: resultId,
        runner_id: runnerId,
        verified_only: verifiedOnly,
        deep_recovery: deepRecovery
      };

      // Add max_depth if specified (not used in deep_recovery mode)
      if (!deepRecovery && maxDepth && parseInt(maxDepth) > 0) {
        requestBody.max_depth = parseInt(maxDepth);
      }

      // Add branch if specified (not used in deep_recovery mode)
      if (!deepRecovery && branch && branch.trim()) {
        requestBody.branch = branch.trim();
      }

      const response = await authenticatedFetch('/deepscan/scan', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (response?.scan_id) {
        const scanMode = deepRecovery ? 'Deep Recovery scan' : (verifiedOnly ? 'Verified secrets scan' : 'Full scan');
        const details = [];
        if (deepRecovery) details.push('deep recovery');
        if (!deepRecovery && maxDepth) details.push(`depth: ${maxDepth}`);
        if (!deepRecovery && branch) details.push(`branch: ${branch}`);
        const detailsStr = details.length > 0 ? ` (${details.join(', ')})` : '';
        addSuccessNotification(`${scanMode} started successfully${detailsStr}`);
        setCurrentScan({
          scan_id: response.scan_id,
          status: 'pending',
          progress: 0,
          progress_message: deepRecovery ? 'Deep Recovery: Cloning repository...' : 'Initializing scan...',
          verified_only: verifiedOnly,
          max_depth: maxDepth ? parseInt(maxDepth) : null,
          branch: branch || null,
          deep_recovery: deepRecovery
        });
        // Refresh history to show the new pending scan
        fetchScanData();
      } else if (response?.error) {
        addErrorNotification(response.error);
      }

    } catch (error) {
      console.error('Error starting scan:', error);
      // Use the error message from the backend if available
      const errorMessage = error?.message || 'Failed to start deep scan';
      addErrorNotification(errorMessage);
    } finally {
      setStartingScan(false);
    }
  };

  /**
   * Cancel a running scan
   */
  const cancelScan = async () => {
    if (!currentScan?.scan_id) return;

    try {
      const response = await authenticatedFetch(
        `/deepscan/scan/${currentScan.scan_id}/cancel`,
        { method: 'POST' }
      );

      if (response?.message) {
        addSuccessNotification('Scan cancelled');
        fetchScanData();
      }

    } catch (error) {
      console.error('Error cancelling scan:', error);
      addErrorNotification('Failed to cancel scan');
    }
  };

  // Don't render if no repository URL
  if (!repositoryUrl) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Deep Scan"
      size="xl"
    >
      <div className="space-y-5">
        {/* Repository Info */}
        <div className="flex items-center justify-between p-3 bg-surface-tertiary rounded-md border border-border">
          <div className="flex items-center gap-2">
            <GitCommit className="w-4 h-4 text-ink-secondary" />
            <a
              href={repositoryUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-caption text-accent hover:text-accent-hover flex items-center gap-1"
            >
              {repositoryUrl?.replace('https://github.com/', '') || 'Unknown repository'}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          <button
            onClick={fetchScanData}
            className="p-1.5 rounded-md hover:bg-surface-secondary text-ink-secondary hover:text-ink transition-base"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Scan Configuration Form - show when no scan running */}
        {!(currentScan?.status === 'running' || currentScan?.status === 'pending') && (
          <DeepScanForm
            config={scanConfig}
            onChange={setScanConfig}
            disabled={startingScan}
          />
        )}

        {/* Scan History */}
        <div className="max-h-[40vh] overflow-y-auto">
          {loading ? (
            <Loading size="md" variant="icon" text="Loading scan data..." />
          ) : (
            <div className="space-y-3">
              <h3 className="text-caption font-medium text-ink-secondary uppercase tracking-wider">
                Scan History ({scanHistory.length})
              </h3>
              {scanHistory.length > 0 ? (
                scanHistory.map((scan) => (
                  <Link
                    key={scan.scan_id}
                    to={`/deepscan/${scan.scan_id}`}
                    className="block bg-surface-tertiary rounded-md p-3 border border-border hover:border-accent/30 transition-base"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-wrap">
                        <StatusBadge status={scan.status} size="sm" />
                        {/* Scan Mode Badge */}
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-tiny ${scan.verified_only === true
                              ? 'bg-accent/10 text-accent'
                              : 'bg-orange-500/10 text-orange-400'
                            }`}
                          title={scan.verified_only === true ? 'Verified only - secrets tested against APIs' : 'All secrets - includes unverified'}
                        >
                          {scan.verified_only === true ? (
                            <ShieldCheck className="w-3 h-3" />
                          ) : (
                            <Shield className="w-3 h-3" />
                          )}
                        </span>
                        {/* Deep Recovery Badge */}
                        {scan.deep_recovery === true && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-tiny bg-red-500/10 text-red-400"
                            title="Deep Recovery mode: scans dangling blobs and deleted files"
                          >
                            <HatGlasses className="w-3 h-3" />
                            <span>Recovery</span>
                          </span>
                        )}
                        <div className="ml-1">
                          <div className="text-caption text-ink">
                            {new Date(scan.created_at).toLocaleString()}
                          </div>
                          {scan.finished_at && (
                            <div className="text-tiny text-ink-tertiary">
                              Duration: {Math.round((new Date(scan.finished_at) - new Date(scan.created_at)) / 1000)}s
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {scan.status === 'completed' && (
                          <div className="text-right">
                            <div className="text-caption">
                              <span className="text-danger font-medium">{scan.verified_secrets_count || 0}</span>
                              <span className="text-ink-tertiary"> / {scan.total_secrets_found || 0}</span>
                            </div>
                          </div>
                        )}
                        <ExternalLink className="w-4 h-4 text-ink-tertiary" />
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="text-center py-8 text-body text-ink-secondary">
                  No previous scans found for this repository.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-3 pt-2 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-caption text-ink-secondary border border-border rounded-md hover:bg-surface-tertiary transition-base"
          >
            Cancel
          </button>
          {currentScan?.status === 'running' || currentScan?.status === 'pending' ? (
            <button
              onClick={cancelScan}
              className="flex items-center gap-2 px-4 py-2 bg-danger text-white text-caption font-medium rounded-md hover:bg-danger/90 transition-base"
            >
              <StopCircle className="w-4 h-4" />
              Stop Scan
            </button>
          ) : (
            <button
              onClick={startScan}
              disabled={startingScan}
              className="flex items-center gap-2 px-4 py-2 bg-accent text-white text-caption font-medium rounded-md hover:bg-accent-hover transition-base disabled:opacity-50"
            >
              {startingScan ? (
                <LoadingSpinner size="sm" />
              ) : scanConfig.deepRecovery ? (
                <HatGlasses className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              {startingScan ? 'Starting...' : scanConfig.deepRecovery ? 'Start Deep Recovery' : (currentScan ? 'New Scan' : 'Start Scan')}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default DeepScanModal;
